import {
  type BrandVariants,
  createLightTheme,
  createTeamsDarkTheme,
  type Theme,
} from '@fluentui/react-components';

/**
 * Identidade visual: o azul-marinho e o dourado do modelo de etiqueta da
 * área. O azul vira a rampa da marca do Fluent (botões, seleção, links), e
 * os temas saem das mesmas funções que geram os do Teams; o dourado destaca
 * a navegação e os banners. Trocar a identidade é trocar estes valores.
 */

/** O azul-marinho da etiqueta (`#152942`) no passo 50; o 80 é o dos botões. */
export const BRAND: BrandVariants = {
  10: '#050b14',
  20: '#08121f',
  30: '#0c1a2d',
  40: '#10223b',
  50: '#152942',
  60: '#193457',
  70: '#1e3f6a',
  80: '#24497a',
  90: '#2f5a92',
  100: '#4170aa',
  110: '#5a86be',
  120: '#7a9fcf',
  130: '#9bb8dd',
  140: '#bacfe8',
  150: '#d6e3f2',
  160: '#eaf1f9',
};

/**
 * No escuro, a mesma rampa dois passos mais clara: botões e textos da marca
 * precisam aparecer sobre o fundo escuro (o tema do Teams usa o passo 70 nos
 * botões e o 100 nos textos).
 */
const DARK_BRAND: BrandVariants = {
  10: BRAND[30],
  20: BRAND[40],
  30: BRAND[50],
  40: BRAND[60],
  50: BRAND[70],
  60: BRAND[80],
  70: BRAND[90],
  80: BRAND[100],
  90: BRAND[110],
  100: BRAND[120],
  110: BRAND[130],
  120: BRAND[140],
  130: BRAND[150],
  140: BRAND[160],
  150: '#f2f6fb',
  160: '#f8fafd',
};

/** O dourado das estrelas e do rodapé da etiqueta. */
export const GOLD = {
  base: '#c9a23c',
  hover: '#d4b057',
  pressed: '#b38f2f',
  soft: 'rgba(201, 162, 60, 0.16)',
} as const;

/** O azul-marinho de fundo da navegação e dos banners. */
export const NAVY = {
  deep: BRAND[30],
  base: BRAND[50],
  raised: BRAND[60],
  hover: BRAND[60],
  pressed: BRAND[40],
  light: BRAND[80],
} as const;

export const lightTheme: Theme = createLightTheme(BRAND);
export const darkTheme: Theme = createTeamsDarkTheme(DARK_BRAND);

/**
 * Superfícies em azul-marinho (navegação, banners): o tema escuro com o
 * fundo azul e o dourado no lugar da cor da marca. Assim texto, ícones,
 * foco e hover já vêm legíveis sobre o azul, pelos tokens de sempre.
 */
const onNavy: Partial<Theme> = {
  colorNeutralBackground1: NAVY.base,
  colorNeutralBackground1Hover: NAVY.hover,
  colorNeutralBackground1Pressed: NAVY.pressed,
  colorNeutralForeground2: '#dbe4ef',
  colorNeutralForeground3: '#a9b9cc',
  colorNeutralStroke1: 'rgba(255, 255, 255, 0.32)',
  colorNeutralStroke2: 'rgba(255, 255, 255, 0.16)',
  colorSubtleBackgroundHover: 'rgba(255, 255, 255, 0.08)',
  colorSubtleBackgroundPressed: 'rgba(255, 255, 255, 0.04)',
  colorTransparentBackgroundHover: 'rgba(255, 255, 255, 0.08)',
  colorCompoundBrandForeground1: GOLD.base,
  colorNeutralForeground2BrandSelected: GOLD.base,
  colorBrandForeground1: GOLD.base,
  // O botão principal fica dourado, com texto azul.
  colorBrandBackground: GOLD.base,
  colorBrandBackgroundHover: GOLD.hover,
  colorBrandBackgroundPressed: GOLD.pressed,
  colorBrandBackgroundSelected: GOLD.pressed,
  colorNeutralForegroundOnBrand: NAVY.deep,
};

/** A navegação lateral: o fundo do `Nav` (Background4) também azul. */
export const sidebarTheme: Theme = {
  ...darkTheme,
  ...onNavy,
  colorNeutralBackground4: NAVY.base,
  colorNeutralBackground4Hover: 'rgba(255, 255, 255, 0.08)',
  colorNeutralBackground4Pressed: 'rgba(255, 255, 255, 0.04)',
};

/** Os banners das páginas de destaque (início e detalhes). */
export const bannerTheme: Theme = { ...darkTheme, ...onNavy };
